import { Edit } from "@mui/icons-material";
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { DateCalendar, PickersDay } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import React from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import usePrimaryColor from "../../../../../../../hooks/usePrimaryColor";
import {
  DosagesSelector,
  labels,
} from "../../../../../components/forms/TherapyDetails";
import useSubmit from "../../../../../../master/species/hooks/useSubmit";
import { useParams } from "react-router-dom";
import {
  SINGLE_BOX_THERAPY,
  SINGLE_THERAPY_KEY,
  THERAPY_DAY_DATES,
  THERAPY_DAY_KEY,
  THERAPY_DAYS_FETCH,
} from "../../../../../../../enums/QueryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { getEarliestDate } from "../../../../../../../utils/DateTimeRepository";

export default function AddOrRemoveTherapiesDialog({ dates = [], days = [] }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const [open, setOpen] = React.useState(false);
  const iconColor = usePrimaryColor();
  const theme = useTheme();
  const url = "/therapy/edit/therapy/days/" + id;
  const submittion = useSubmit(null, url, [THERAPY_DAYS_FETCH, id]);
  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);
  const methods = useForm();
  const { invalidate } = useInvalidateAllTherapies(id);
  const title = t("AddOrRemoveTherapies");

  if (dates.length == 0 || !dates) {
    return null;
  }

  async function handleSubmit(data) {
    await submittion.submitCallback(data);
    invalidate();
  }

  return (
    <FormProvider {...methods}>
      <IconButton
        title={title}
        onClick={() => handleOpen()}
        sx={{
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "scale(1.05)",
          },
        }}
      >
        <Edit />
      </IconButton>

      <Dialog open={open} fullWidth onClose={handleClose} maxWidth={"md"}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogTitle>{title}</DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 1 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "16px",
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.4,
                    ),
                    height: "100%",
                  }}
                >
                  <DosagesSelector />
                  <Typography variant="body2" color="text.secondary" mt={2}>
                    {t("AddOrRemoveTherapiesUserHint")}
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1,
                    borderRadius: "16px",
                    backgroundColor: alpha(
                      theme.palette.background.default,
                      0.4,
                    ),
                  }}
                >
                  <CalendarComponent dates={dates} days={days} />
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
            <Button variant="contained" onClick={handleClose} type="submit">
              {t("Save")}
            </Button>
            <Button variant="outlined" color="error" onClick={handleClose}>
              {t("Cancel")}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </FormProvider>
  );
}

function CalendarComponent({ dates = [], days = [] }) {
  const methods = useFormContext();
  const theme = useTheme();

  function getCompletedDates() {
    return days
      .filter((d) => d.eveningDone && d.morningDone)
      .map((value) => value.date);
  }

  function handleDisableDates(date) {
    return getCompletedDates().includes(dayjs(date).format("DD/MM/YYYY"));
  }

  function getPreselectedDates() {
    return days.map((value) => value.date);
  }

  React.useEffect(() => {
    const watchedDates = methods.watch(labels.dates) || [];
    getPreselectedDates().forEach((date) => {
      if (!watchedDates.includes(date)) {
        watchedDates.push(date);
      }
    });
    methods.setValue(labels.dates, watchedDates);
  }, []);

  const watchedDates = methods.watch(labels.dates) || [];
  const selectedCount = watchedDates.length;

  return (
    <Box>
      <DateCalendar
        value={null}
        referenceDate={getEarliestDate(dates, "DD/MM/YYYY")}
        shouldDisableDate={handleDisableDates}
        sx={{
          width: "100%",
          maxHeight: "320px",
          "& .MuiPickersDay-root": {
            fontSize: "0.875rem",
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "scale(1.02)",
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          },
        }}
        slots={{
          day: (props) => {
            const formatted = props.day.format("DD/MM/YYYY");
            const isSelected = watchedDates.includes(formatted);
            const isDisabled = props.disabled;

            const handleClick = () => {
              if (isDisabled) return;

              const updated = isSelected
                ? watchedDates.filter((d) => d !== formatted)
                : [...watchedDates, formatted];

              methods.setValue(labels.dates, updated, {
                shouldDirty: true,
                shouldValidate: true,
              });
            };

            return (
              <PickersDay
                {...props}
                selected={isSelected}
                onClick={handleClick}
                disabled={isDisabled}
              />
            );
          },
        }}
      />
      <Legend />
    </Box>
  );
}

function Legend() {
  const { t } = useTranslation();
  const theme = useTheme();
  const buttonSize = 18;
  return (
    <Box
      sx={{
        mt: 1.5,
        pt: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: buttonSize,
            height: buttonSize,
            borderRadius: "12px",
            backgroundColor: alpha(
              theme.palette.action.disabledBackground,
              0.7,
            ),
            opacity: 0.5,
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {t("Completed")}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: buttonSize,
            height: buttonSize,
            borderRadius: "12px",
            backgroundColor: theme.palette.primary.main,
          }}
        />
        <Typography variant="caption" color="text.secondary">
          {t("Selected")}
        </Typography>
      </Box>
    </Box>
  );
}

export function useInvalidateAllTherapies({ id }) {
  const queryClient = useQueryClient();
  function invalidate() {
    queryClient.invalidateQueries([THERAPY_DAYS_FETCH, id]);
    queryClient.invalidateQueries([SINGLE_THERAPY_KEY, id]);
    queryClient.invalidateQueries([THERAPY_DAYS_FETCH, id]);
    queryClient.invalidateQueries([THERAPY_DAY_DATES, id]);
    queryClient.invalidateQueries([THERAPY_DAY_KEY, id]);
    queryClient.invalidateQueries([SINGLE_BOX_THERAPY, id]);
  }

  return {
    invalidate,
  };
}
